/**
 * Выводы индикаторов уровней: профиль объёма и карта ликвидности.
 *
 * Оба рисуют полосы поперёк цены, и обоим человек задаёт один вопрос:
 * «а где я сейчас относительно этого?». Табло отвечает прямо: где
 * главный уровень и зона стоимости, сколько до ближайшей ликвидности
 * сверху и снизу — и что это обычно значит.
 */

import type { Candle } from '@specter/types';
import type { LiquidityLevel, ProfileBand } from '@specter/rules';
import { percentWords, priceWords, situationRows, type Situation } from './explain.js';
import type { ReadoutRow } from './overlays.js';

/** Доля объёма в зоне стоимости: привычные семьдесят процентов. */
const VALUE_AREA = 0.7;

export interface ProfileSummary {
  readonly poc: ProfileBand;
  readonly valueLow: number;
  readonly valueHigh: number;
}

/** Главный уровень и зона стоимости по полосам профиля. */
export function profileSummary(bands: readonly ProfileBand[]): ProfileSummary | null {
  if (bands.length === 0) return null;
  const total = bands.reduce((sum, band) => sum + band.share, 0);
  if (total <= 0) return null;
  const ordered = [...bands].sort((a, b) => b.share - a.share);
  const poc = ordered[0]!;
  let taken = 0;
  let valueLow = poc.low;
  let valueHigh = poc.high;
  for (const band of ordered) {
    if (taken >= total * VALUE_AREA) break;
    taken += band.share;
    valueLow = Math.min(valueLow, band.low);
    valueHigh = Math.max(valueHigh, band.high);
  }
  return { poc, valueLow, valueHigh };
}

export function explainProfile(candles: readonly Candle[], bands: readonly ProfileBand[]): ReadoutRow[] {
  const last = candles.at(-1);
  const summary = profileSummary(bands);
  if (!last || !summary) return [];
  const price = last.close;
  const pocMid = (summary.poc.low + summary.poc.high) / 2;
  const toPoc = ((pocMid - price) / price) * 100;

  let situation: Situation;
  if (price >= summary.poc.low && price <= summary.poc.high) {
    situation = {
      title: 'У главного уровня',
      tone: 'muted',
      text: 'Цена стоит на самом проторгованном уровне — здесь рынок чаще всего топчется. Выход отсюда обычно резкий.',
    };
  } else if (price > summary.valueHigh) {
    situation = {
      title: 'Выше зоны стоимости',
      tone: 'bull',
      text: 'Цена выше, чем торговали большую часть времени, — покупатели приняли новые цены. Если вернётся в зону, рост скорее был ложным.',
    };
  } else if (price < summary.valueLow) {
    situation = {
      title: 'Ниже зоны стоимости',
      tone: 'bear',
      text: 'Цена ниже, чем торговали большую часть времени, — продавцы продавили новые цены. Если вернётся в зону, падение скорее было ложным.',
    };
  } else {
    situation = {
      title: 'Внутри зоны стоимости',
      tone: 'muted',
      text: 'Цена там, где торговали большую часть времени. Края зоны — ближайшие опоры, главный уровень притягивает цену.',
    };
  }

  return [
    ...situationRows(situation),
    { label: 'Главный уровень', value: `${priceWords(pocMid)} · ${percentWords(toPoc)}` },
    { label: 'Зона стоимости', value: `${priceWords(summary.valueLow)} – ${priceWords(summary.valueHigh)}` },
  ];
}

export function explainLiquidity(candles: readonly Candle[], levels: readonly LiquidityLevel[]): ReadoutRow[] {
  const last = candles.at(-1);
  if (!last) return [];
  const price = last.close;
  const alive = levels.filter((level) => level.to === null && level.evictedAt === null);
  const above = alive.filter((level) => level.price > price).sort((a, b) => a.price - b.price);
  const below = alive.filter((level) => level.price < price).sort((a, b) => b.price - a.price);
  const up = above[0];
  const down = below[0];
  const upDistance = up ? ((up.price - price) / price) * 100 : null;
  const downDistance = down ? ((price - down.price) / price) * 100 : null;

  let situation: Situation;
  if (upDistance === null && downDistance === null) {
    situation = { title: 'Рядом пусто', tone: 'muted', text: 'Живых скоплений стопов рядом с ценой нет — цене не за чем тянуться.' };
  } else if (downDistance === null || (upDistance !== null && upDistance * 2 < downDistance)) {
    situation = {
      title: 'Ближе сверху',
      tone: 'bull',
      text: `Ближайшее скопление стопов — сверху, в ${percentWords(upDistance!).replace('+', '')} от цены. Цена часто сначала идёт за ближней ликвидностью.`,
    };
  } else if (upDistance === null || downDistance * 2 < upDistance) {
    situation = {
      title: 'Ближе снизу',
      tone: 'bear',
      text: `Ближайшее скопление стопов — снизу, в ${percentWords(downDistance).replace('+', '')} от цены. Цена часто сначала идёт за ближней ликвидностью.`,
    };
  } else {
    situation = {
      title: 'С обеих сторон',
      tone: 'muted',
      text: 'Скопления стопов сверху и снизу на похожем расстоянии — явной цели у цены нет.',
    };
  }

  const rows: ReadoutRow[] = [...situationRows(situation)];
  if (up && upDistance !== null) rows.push({ label: 'Сверху', value: `${priceWords(up.price)} · ${percentWords(upDistance, 2)}`, tone: 'down' });
  if (down && downDistance !== null) rows.push({ label: 'Снизу', value: `${priceWords(down.price)} · ${percentWords(-downDistance, 2)}`, tone: 'up' });
  rows.push({ label: 'Живых уровней', value: `${above.length} сверху · ${below.length} снизу`, tone: 'muted' });
  return rows;
}
